import { makeGetRequest, makePostRequest } from "../makeRequest";
import appConfig from "../../config/app.config";

/**
 * Neo API service functions for fetching data from Redshift
 */

/**
 * Get remittance transaction summary
 * @param {number} days - Number of days to look back (default: 20)
 * @returns {Promise<Object>} Remittance summary data
 */
export const getRemittanceSummary = async (days = 20) => {
  try {
    const url = `${appConfig.apiUrl.neosummary}?days=${days}`;
    const response = await makeGetRequest(url);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Get partner-wise remittance summary
 * @param {number} days - Number of days to look back (default: 20)
 * @returns {Promise<Object>} Partner-wise remittance summary data
 */
export const getPartnerWiseRemittanceSummary = async (days = 20) => {
  try {
    const url = `${appConfig.apiUrl.neopartner}?days=${days}`;
    const response = await makeGetRequest(url);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Get remittance transaction details
 * @param {Object} params - Query parameters
 * @param {number} params.days - Number of days to look back (default: 20)
 * @param {number} params.limit - Number of records to return (default: 100)
 * @param {number} params.offset - Number of records to skip (default: 0)
 * @returns {Promise<Object>} Remittance transaction details
 */
export const getRemittanceTransactions = async (params = {}) => {
  try {
    const { days = 20, limit = 100, offset = 0 } = params;
    const url = `${appConfig.apiUrl.neotransactions}?days=${days}&limit=${limit}&offset=${offset}`;
    const response = await makeGetRequest(url);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Get partner trading meta data
 * @returns {Promise<Object>} Partner trading meta data
 */
export const getPartnerTradingMetaData = async () => {
  try {
    const url = `${appConfig.apiUrl.neotradingmeta}`;
    const response = await makeGetRequest(url);
    return response;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Get day-wise remittance summary
 * @returns {Promise<Object>} Day-wise remittance summary data
 */
export const getDayWiseRemittanceSummary = async () => {
  try {
    const url = `${appConfig.apiUrl.neodaywise}`;
    const response = await makeGetRequest(url);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Get profit and loss summary for remittance
 * @returns {Promise<Object>} Profit and loss summary data
 */
export const getProfitAndLossSummary = async () => {
  try {
    const url = `${appConfig.apiUrl.remittanceProfitAndLossSummary}`;
    const response = await makeGetRequest(url);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Get event types eligible for manual remittance entries
 * @returns {Promise<Object>} Eligible manual event types
 */
export const getEligibleManualRemittanceEventTypes = async () => {
  try {
    const url = `${appConfig.apiUrl.eligibleManualRemittanceEventTypes}`;
    const response = await makeGetRequest(url);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Create a manual remittance event
 * @param {Object} payload - Manual remittance event payload
 * @returns {Promise<Object>} Created manual remittance event
 */
export const createManualRemittanceEvent = async (payload) => {
  try {
    const url = `${appConfig.apiUrl.manualRemittanceEvent}`;
    const response = await makePostRequest(url, payload);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Get Neo data with filters
 * @param {Object} filters - Query filters
 * @returns {Promise<Object>} Neo data
 */
export const getNeoData = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && filters[key] !== null) {
        queryParams.append(key, filters[key]);
      }
    });

    const url = `${appConfig.apiUrl.neodata}?${queryParams.toString()}`;
    const response = await makeGetRequest(url);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Get Neo summary statistics
 * @param {Object} filters - Query filters
 * @returns {Promise<Object>} Neo summary data
 */
export const getNeoSummary = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && filters[key] !== null) {
        queryParams.append(key, filters[key]);
      }
    });

    const url = `${appConfig.apiUrl.neosummary}?${queryParams.toString()}`;
    const response = await makeGetRequest(url);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

export const getFundsDistribution = async () => {
  try {
    const url = `${appConfig.apiUrl.neofundsdistribution}?job_name=PartnerWiseDistributionForOfframping`;
    const response = await makePostRequest(url, {});
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};

/**
 * Get partner balances from external APIs
 * @returns {Promise<Object>} Partner balances data
 */
export const getPartnerBalances = async () => {
  try {
    const url = `${appConfig.apiUrl.neopartnerbalances}`;
    const response = await makeGetRequest(url);
    return response.data;
  } catch (error) {
    return error?.response?.data || error?.response || error;
  }
};
